import type {
  LHCDetails,
  LhcItemDetail,
  LhcItemRelatedDeliveryOrder,
} from '@diamond/shared/types';
import {
  formatCurrencyIdr,
  formatShortDate,
  unixToHumanDate,
} from '@diamond/shared/utils';

import type { LHCDetailInvoiceRowData, LHCRowData } from '../types';

/** Re-export for consumers that need the same IDR format (e.g. LHCDetailSummary) */
export { formatCurrencyIdr as formatCurrency } from '@diamond/shared/utils';

/**
 * Maps a single LHC list item from API response to table row data.
 * Transforms snake_case API fields to camelCase UI fields, formats dates/currency,
 * and adds row number and raw values for Edit modal.
 *
 * @param item - Raw LHC item from getList API
 * @param index - Index within current page
 * @param pageOffset - (page - 1) * size for correct row numbering
 * @returns LHCRowData ready for GridTable
 */
export function mapLHCItemToRowData(
  item: LHCDetails,
  index: number,
  pageOffset: number
): LHCRowData {
  return {
    id: item.id,
    no: pageOffset + index + 1,
    lhcDate: formatShortDate(item.lhc_date),
    lhcNo: item.lhc_no || '-',
    plant: item.plant_name || '-',
    totalInvoice: item.total_invoice_count ?? 0,
    totalInvoiceValue: formatCurrencyIdr(item.total_invoice_amount),
    mutationValue: formatCurrencyIdr(item.total_mutation_amount),
    totalDifference: formatCurrencyIdr(item.total_difference_amount),
    lastUpdated: item.updated_at
      ? unixToHumanDate(item.updated_at, "d MMM yy '·' HH.mm")
      : '-',
    collectionPlantId: item.collection_plant_id,
    lhcDateRaw: item.lhc_date,
    updatedAtUnix: item.updated_at ?? null,
  };
}

function mapParentRow(
  item: LhcItemDetail,
  rowNumber: number,
  plantName: string
): LHCDetailInvoiceRowData {
  return {
    rowId: item.id,
    parentId: '',
    no: rowNumber,
    poNumber: item.po_number || '-',
    plant: plantName || '-',
    invoiceDate: item.invoice_date
      ? unixToHumanDate(item.invoice_date, 'd MMM yy')
      : '-',
    deliveryOrderNo: item.delivery_order_number || '-',
    invoiceNo: item.invoice_no || '-',
    billTo: item.sap_no_bilto ? String(item.sap_no_bilto) : '-',
    customerName: item.customer_name || '-',
    outletName: item.outlet_name || '-',
    poValue: formatCurrencyIdr(item.po_amount),
    soValue: formatCurrencyIdr(item.so_amount),
    invoiceValue: formatCurrencyIdr(item.invoice_amount),
    differenceAmount: formatCurrencyIdr(item.difference_amount),
    mutationValue: formatCurrencyIdr(item.mutation_amount),
    bankMutationNo:
      item.bank_mutations?.map((m) => m.bank_mutation_no).join(', ') || '-',
    depositUsed: formatCurrencyIdr(item.deposit_used_amount),
    hasPaymentProof: (item.payment_proof_ids?.length ?? 0) > 0,
    bank: item.bank || '-',
    remark: item.remark || '-',
    isChildRow: false,
    hasChildren: (item.related_delivery_orders?.length ?? 0) > 1,
  };
}

function mapChildRow(
  order: LhcItemRelatedDeliveryOrder,
  parentId: string
): LHCDetailInvoiceRowData {
  return {
    rowId: '',
    parentId,
    no: '',
    poNumber: order.lhc_no || '',
    plant: order.plant_name || '-',
    invoiceDate: order.billing_date
      ? unixToHumanDate(order.billing_date, 'd MMM yy')
      : '-',
    deliveryOrderNo: order.delivery_order_number || '-',
    invoiceNo: order.billing_number || '-',
    billTo: '',
    customerName: '',
    outletName: '',
    poValue: '',
    soValue: formatCurrencyIdr(order.so_total_amount),
    invoiceValue: formatCurrencyIdr(order.billing_total_amount),
    differenceAmount: '',
    mutationValue: '',
    bankMutationNo: '',
    depositUsed: '',
    hasPaymentProof: false,
    bank: '',
    remark: '',
    isChildRow: true,
    hasChildren: false,
  };
}

/**
 * Flattens LHC invoice items with their related_delivery_orders into a flat row array.
 * Parent rows appear first, followed by their child rows (gray background for other delivery orders).
 * Used by LHCDetail table to display hierarchical invoice data.
 *
 * @param items - Raw invoice items from getItems API (LhcItemDetail[])
 * @param plantName - Plant name from parent LHC for display
 * @returns Flat array of LHCDetailInvoiceRowData (parent + child rows)
 */
export function mapInvoiceItemsToRows(
  items: LhcItemDetail[],
  plantName: string
): LHCDetailInvoiceRowData[] {
  const rows: LHCDetailInvoiceRowData[] = [];
  let rowNumber = 1;

  for (const item of items) {
    rows.push(mapParentRow(item, rowNumber, plantName));
    rowNumber++;

    if ((item.related_delivery_orders?.length ?? 0) > 1) {
      for (const order of item.related_delivery_orders) {
        rows.push(mapChildRow(order, item.id));
      }
    }
  }

  return rows;
}
